// injectXingHeader.js — client-side MP3 rewrite that guarantees accurate
// duration + seekability in WKWebView.
//
// Why this exists
// ---------------
// Our TTS endpoint returns VBR MP3 with NO Xing/Info/VBRI header. WebKit's
// MP3 demuxer falls back to `fileSize × 8 / firstFrameBitrate` for
// duration — a CBR assumption that's badly wrong for VBR streams whose
// opening frames happen to sit above the file's average bitrate. Observed
// in the wild: a 6:48 brief renders with `audio.duration = 230` (3:50).
//
// A wrong duration is only cosmetic for linear playback (WebKit will
// happily decode frames past its stated end). The real damage is that the
// HTMLMediaElement spec clamps programmatic seeks:
//
//   "If the media resource has a duration of finite value, set new playback
//    position to the lesser of the given position and the duration."
//
// When we try `el.currentTime = 300` on a 230-second-declared stream,
// WebKit clamps to 230, fires `seeked`, notices `currentTime >= duration`,
// fires `ended`, and stops playback. The user's scrubber drag to 5:00
// becomes "jump to the end".
//
// We cannot defeat the spec clamp. We CAN fix the duration WebKit sees.
//
// The Xing header is an MP3 side-channel invented for exactly this
// problem. LAME, ffmpeg, and every modern MP3 encoder write one by
// default; some TTS pipelines strip it. It lives inside a single "silent"
// MPEG audio frame prepended to the real stream. Its "frames" field tells
// any VBR-aware decoder the total number of audio frames, from which the
// true duration is `frames × samplesPerFrame / sampleRate` — accurate to
// the frame regardless of bitrate variation.
//
// This module parses the original stream, counts its frames (reusing the
// same logic as mp3Duration.js), synthesizes a silent Xing info frame
// that matches the stream's version/layer/samplerate/channel, and splices
// it in. The returned buffer is a drop-in replacement we hand to
// URL.createObjectURL — WebKit now reads the Xing header on load and
// reports the correct duration, which makes seeks work anywhere in the
// stream.
//
// Safety
// ------
//  * If parsing fails, we return the original bytes — never break audio.
//  * Minimum 4 valid consecutive frames required before we'll trust the
//    scan.
//  * The synthesized silent frame is valid MPEG-1/2/2.5 per spec; worst
//    case a decoder that doesn't understand Xing just plays ~26 ms of
//    silence before the real audio starts. That silence is below the
//    user-perceptible threshold.
//
// Why we DON'T skip when an existing Xing/Info tag is found
// ---------------------------------------------------------
// /api/tts issues one request per ~4200-char chunk and concatenates the
// resulting MP3 blobs byte-for-byte. Each of those blobs carries its own
// Xing header reporting the frame count for ITS chunk only. In the
// concatenated stream, the first Xing header therefore lies about the
// total duration: it describes ~3 minutes of audio even though the real
// stream is 6–8 minutes. WKWebView reads that first header and sets
// audio.duration to ~3:XX, re-triggering the same seek-clamp-ends bug
// that Xing injection was meant to fix.
//
// We cannot detect this reliably from the existing tag alone (the chunk
// Xing is internally valid — it just describes a shorter stream). The
// safe fix is to ALWAYS prepend our own Xing frame, computed from a
// full-stream frame scan. Browsers read the first Xing header they see,
// which is now ours. The per-chunk Xing frames downstream remain valid
// silent MPEG frames and contribute ~26 ms of silence per chunk
// boundary, which they already did before injection.

// Bitrate table (kbps). Same shape as mp3Duration.js — see that file for
// comments. Duplicated here (not imported) to keep this module
// standalone; both tables are small and change essentially never.
const BITRATE_TABLE = {
  1: {
    1: [0, 32, 64, 96, 128, 160, 192, 224, 256, 288, 320, 352, 384, 416, 448, -1],
    2: [0, 32, 48, 56,  64,  80,  96, 112, 128, 160, 192, 224, 256, 320, 384, -1],
    3: [0, 32, 40, 48,  56,  64,  80,  96, 112, 128, 160, 192, 224, 256, 320, -1],
  },
  2: {
    1: [0, 32, 48, 56,  64,  80,  96, 112, 128, 144, 160, 176, 192, 224, 256, -1],
    2: [0,  8, 16, 24,  32,  40,  48,  56,  64,  80,  96, 112, 128, 144, 160, -1],
    3: [0,  8, 16, 24,  32,  40,  48,  56,  64,  80,  96, 112, 128, 144, 160, -1],
  },
};

const SAMPLERATE_TABLE = {
  '1':   [44100, 48000, 32000, -1],
  '2':   [22050, 24000, 16000, -1],
  '2.5': [11025, 12000,  8000, -1],
};

const SAMPLES_PER_FRAME = {
  1: { 1: 384, 2: 1152, 3: 1152 },
  2: { 1: 384, 2: 1152, 3:  576 },
};

// Side-info length (bytes) between the 4-byte MPEG header and the audio
// payload. This is where the Xing tag lives. Values per the MPEG audio
// spec: depends on version and mono-vs-multi-channel.
function sideInfoLength(versionGroup, channelMode) {
  const mono = channelMode === 3;
  if (versionGroup === 1) return mono ? 17 : 32;
  return mono ? 9 : 17;
}

function frameByteSize(versionGroup, layer, bitrateKbps, sampleRate, padding) {
  if (layer === 1) {
    return Math.floor((12 * bitrateKbps * 1000 / sampleRate + padding) * 4);
  }
  const factor = versionGroup === 1 ? 144 : 72;
  return Math.floor(factor * bitrateKbps * 1000 / sampleRate) + padding;
}

function skipId3v2(u8) {
  if (u8.length < 10) return 0;
  if (u8[0] !== 0x49 || u8[1] !== 0x44 || u8[2] !== 0x33) return 0;
  const size = ((u8[6] & 0x7F) << 21)
             | ((u8[7] & 0x7F) << 14)
             | ((u8[8] & 0x7F) <<  7)
             |  (u8[9] & 0x7F);
  return Math.min(u8.length, 10 + size);
}

// Parse a single frame header at offset. Returns null on anything bogus.
function parseFrameHeader(u8, offset) {
  if (offset + 4 > u8.length) return null;
  const b0 = u8[offset];
  const b1 = u8[offset + 1];
  if (b0 !== 0xFF || (b1 & 0xE0) !== 0xE0) return null;
  const b2 = u8[offset + 2];
  const b3 = u8[offset + 3];

  const versionBits = (b1 >> 3) & 0x03;  // 00=2.5, 01=res, 10=2, 11=1
  const layerBits   = (b1 >> 1) & 0x03;  // 00=res, 01=III, 10=II, 11=I
  if (versionBits === 1 || layerBits === 0) return null;

  const versionKey   = versionBits === 3 ? '1' : versionBits === 2 ? '2' : '2.5';
  const versionGroup = versionKey === '1' ? 1 : 2;
  const layer        = 4 - layerBits;

  const bitrateIndex    = (b2 >> 4) & 0x0F;
  const sampleRateIndex = (b2 >> 2) & 0x03;
  const padding         = (b2 >> 1) & 0x01;
  const channelMode     = (b3 >> 6) & 0x03; // 00=stereo,01=joint,10=dual,11=mono

  const bitrate    = BITRATE_TABLE[versionGroup][layer][bitrateIndex];
  const sampleRate = SAMPLERATE_TABLE[versionKey][sampleRateIndex];
  if (!bitrate || bitrate <= 0 || !sampleRate || sampleRate <= 0) return null;

  const samplesPerFrame = SAMPLES_PER_FRAME[versionGroup][layer];
  const frameSize = frameByteSize(versionGroup, layer, bitrate, sampleRate, padding);
  if (frameSize < 4) return null;

  return {
    versionKey, versionGroup, layer,
    bitrateKbps: bitrate,
    sampleRate, samplesPerFrame,
    channelMode, padding,
    frameSize,
    bitrateIndex, sampleRateIndex,
  };
}

// Note: we used to gate injection on "does the first frame already carry
// a Xing/Info/VBRI header?" and skip when the answer was yes. That check
// was removed because ElevenLabs responses returned by /api/tts are
// concatenated per-chunk MP3s, and any pre-existing first-frame Xing
// describes only the first chunk — leading WebKit to set audio.duration
// to a truncated value. See the module header for the full rationale.
// We now always scan + inject.

// Pick a target bitrate for the synthesized silent frame. We need the
// frame to be large enough to hold our Xing tag (max ~120 bytes including
// all optional fields), and want to stay conservative on decoder
// compatibility. 128 kbps for MPEG-1, 64 kbps for MPEG-2/2.5 both give
// >350-byte frames at common samplerates and are universally supported.
function targetSilentBitrate(versionGroup, layer) {
  if (versionGroup === 1) {
    if (layer === 1) return 192;
    return 128;
  }
  if (layer === 1) return 96;
  return 64;
}

function buildSilentXingFrame(origHdr, totalFrames, totalAudioBytes) {
  const { versionKey, versionGroup, layer, sampleRate, channelMode } = origHdr;
  const targetKbps = targetSilentBitrate(versionGroup, layer);
  const bitrateIdx = BITRATE_TABLE[versionGroup][layer].indexOf(targetKbps);
  if (bitrateIdx < 0) return null;
  const sampleRateIdx = SAMPLERATE_TABLE[versionKey].indexOf(sampleRate);
  if (sampleRateIdx < 0) return null;

  const padding = 0;
  const frameSize = frameByteSize(versionGroup, layer, targetKbps, sampleRate, padding);
  if (frameSize < 40) return null; // not enough room for the tag

  const out = new Uint8Array(frameSize); // zero-filled

  // MPEG header. Bit layout across 4 bytes:
  //   Byte 0: 11111111                         (sync)
  //   Byte 1: 111 VV LL P  (3 sync bits + version + layer + protection)
  //   Byte 2: BBBB SS D X  (bitrate + samplerate + padding + private)
  //   Byte 3: CC MM CpOr EE (channel + mode-ext + copyright/orig + emphasis)
  const versionBits = versionKey === '1' ? 3 : (versionKey === '2' ? 2 : 0);
  const layerBits   = layer === 1 ? 3 : (layer === 2 ? 2 : 1);
  const protectionBit = 1; // 1 = no CRC
  out[0] = 0xFF;
  out[1] = 0xE0 | (versionBits << 3) | (layerBits << 1) | protectionBit;
  out[2] = (bitrateIdx << 4) | (sampleRateIdx << 2) | (padding << 1) | 0;
  // For joint-stereo MPEG-1 L-III, mode_extension matters; leaving it at
  // zero is equivalent to "intensity off, MS off" which is the safest
  // (and perfectly legal) configuration for a silent frame.
  out[3] = (channelMode << 6) | 0;

  // Side info stays all zeros — that's the legitimate encoding of a
  // frame with no encoded audio data. Decoders render it as silence.

  // Xing header at offset 4 + sideInfoLen.
  const sideLen = sideInfoLength(versionGroup, channelMode);
  const tagOff = 4 + sideLen;
  if (tagOff + 16 > frameSize) return null;

  // Magic. "Xing" signals VBR; "Info" is the CBR variant — but every
  // decoder I've dug through treats the two interchangeably for the
  // purpose of reading frame/byte counts, and writing "Xing" is the
  // correct choice for our use case since the underlying stream actually
  // is variable.
  out[tagOff + 0] = 0x58; // 'X'
  out[tagOff + 1] = 0x69; // 'i'
  out[tagOff + 2] = 0x6E; // 'n'
  out[tagOff + 3] = 0x67; // 'g'

  // Flags (big-endian u32): 0x0003 = frames + bytes present. No TOC,
  // no quality — WebKit doesn't need them for duration, and the TOC
  // would require a second pass over the original stream.
  out[tagOff + 4] = 0x00;
  out[tagOff + 5] = 0x00;
  out[tagOff + 6] = 0x00;
  out[tagOff + 7] = 0x03;

  // Frames field — total frames INCLUDING this synthesized info frame.
  // Per the spec most decoders include the info frame in the count;
  // WebKit's implementation matches this.
  const framesTotal = totalFrames + 1;
  out[tagOff +  8] = (framesTotal >>> 24) & 0xFF;
  out[tagOff +  9] = (framesTotal >>> 16) & 0xFF;
  out[tagOff + 10] = (framesTotal >>>  8) & 0xFF;
  out[tagOff + 11] =  framesTotal         & 0xFF;

  // Bytes field — total stream size in bytes INCLUDING this info frame.
  const bytesTotal = totalAudioBytes + frameSize;
  out[tagOff + 12] = (bytesTotal >>> 24) & 0xFF;
  out[tagOff + 13] = (bytesTotal >>> 16) & 0xFF;
  out[tagOff + 14] = (bytesTotal >>>  8) & 0xFF;
  out[tagOff + 15] =  bytesTotal         & 0xFF;

  return out;
}

// Public entry point.
//
// Takes the raw MP3 bytes (ArrayBuffer or Uint8Array), returns a new
// Uint8Array with a Xing info frame prepended (after any ID3v2 tag).
// If the input already carries a Xing/Info/VBRI header, or parsing fails,
// returns the input bytes untouched.
export function injectXingHeader(input) {
  if (!input) return new Uint8Array(0);
  const src = input instanceof Uint8Array
    ? input
    : new Uint8Array(input);
  if (src.length < 32) return src;

  const audioStart = skipId3v2(src);

  // Find the first real frame. We resync rather than assume audioStart
  // is a frame boundary because some encoders emit garbage bytes between
  // the ID3v2 tag and the first frame.
  let frameStart = -1;
  let firstHdr = null;
  const scanLimit = Math.min(src.length - 4, audioStart + 65536);
  for (let i = audioStart; i <= scanLimit; i++) {
    if (src[i] !== 0xFF || (src[i + 1] & 0xE0) !== 0xE0) continue;
    const hdr = parseFrameHeader(src, i);
    if (!hdr || i + hdr.frameSize + 4 > src.length) continue;
    // Double-check: next frame header must also parse, with compatible
    // version/layer/samplerate. Guards against sync-word false positives.
    const next = parseFrameHeader(src, i + hdr.frameSize);
    if (!next) continue;
    if (next.versionKey !== hdr.versionKey) continue;
    if (next.layer !== hdr.layer) continue;
    if (next.sampleRate !== hdr.sampleRate) continue;
    frameStart = i;
    firstHdr = hdr;
    break;
  }
  if (frameStart < 0 || !firstHdr) return src;

  // Count frames and bytes across the rest of the stream.
  //
  // /api/tts concatenates per-chunk MP3 files byte-for-byte. Each ElevenLabs
  // response carries its own ID3v2 tag + Xing info frame at the front, so
  // the concatenated stream contains mid-file ID3v2 headers and silent
  // Xing frames at every chunk boundary. An earlier version of this loop
  // bailed (`if (!hdr) break`) at the first non-frame byte, which meant we
  // were only counting frames up to the SECOND chunk's ID3v2 tag — the
  // injected Xing then advertised chunk-1 duration only and WebKit's
  // `audio.duration` came out as ~chunk-1 length. The seek clamp pinned
  // the scrubber there even though the rest of the audio plays fine.
  //
  // The fix is to resync the way probeMp3Duration does: skip mid-stream
  // ID3v2 tags explicitly, and byte-walk through anything else until we
  // find the next valid frame. We still require the "next frame after
  // this one also looks valid" double-check from the initial sync, to
  // avoid sync-word collisions inside random bytes turning into phantom
  // frames.
  let totalFrames = 0;
  let totalBytes = 0;
  let pos = frameStart;
  while (pos + 4 <= src.length) {
    // A concatenated chunk may start with an ID3v2 tag. Skip it wholesale.
    if (src[pos] === 0x49 && src[pos + 1] === 0x44 && src[pos + 2] === 0x33 /* "ID3" */) {
      const afterTag = pos + skipId3v2(src.subarray(pos));
      if (afterTag <= pos) { pos++; continue; }
      pos = afterTag;
      continue;
    }
    const hdr = parseFrameHeader(src, pos);
    if (!hdr || pos + hdr.frameSize > src.length) {
      pos++;
      continue;
    }
    totalFrames++;
    totalBytes += hdr.frameSize;
    pos += hdr.frameSize;
  }
  if (totalFrames < 4) return src;

  const xingFrame = buildSilentXingFrame(firstHdr, totalFrames, totalBytes);
  if (!xingFrame) return src;

  // Assemble output:
  //   [ID3v2 (if any) / preamble]  [Xing frame]  [original audio frames]
  // Everything before `frameStart` (ID3v2 + any resync garbage) is
  // preserved verbatim — decoders that rely on it (metadata, replaygain)
  // keep seeing their tags at the same byte offsets.
  const out = new Uint8Array(frameStart + xingFrame.length + (src.length - frameStart));
  out.set(src.subarray(0, frameStart), 0);
  out.set(xingFrame, frameStart);
  out.set(src.subarray(frameStart), frameStart + xingFrame.length);
  return out;
}
